import type { CSSProperties, ReactNode } from 'react'
import { Link } from 'react-router-dom'

import type { PaletteColour } from '@anytracker/core'

import { paletteVar, type Shade } from '../charts/colours.js'
import styles from './Layout.module.css'

/*
 * The Meadow layout pieces.
 *
 * A screen is a column of full-width bands on a pale page (Layout.module.css).
 * These components are that structure and nothing else: no data, no domain.
 * Every screen composes the same few, which is what keeps forty rows across
 * eleven screens lined up on the same gutter.
 */

// ─── Icons ───────────────────────────────────────────────────────────────────

/** A stroke icon on the design's 24-unit grid. Square caps, like everything else. */
export function Icon({
  path,
  className,
}: {
  readonly path: string
  readonly className?: string | undefined
}) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="square"
      aria-hidden="true"
    >
      <path d={path} />
    </svg>
  )
}

export const ICONS = {
  chevronRight: 'M9 6l6 6-6 6',
  chevronLeft: 'M15 6l-6 6 6 6',
  chevronDown: 'M6 9l6 6 6-6',
  arrowRight: 'M5 12h14M13 6l6 6-6 6',
  plus: 'M12 5v14M5 12h14',
  minus: 'M5 12h14',
  close: 'M6 6l12 12M18 6 6 18',
  filter: 'M4 7h16M7 12h10M10 17h4',
  clock: 'M12 4a8 8 0 1 0 0 16 8 8 0 0 0 0-16zM12 8v4l3 2',
} as const

function Chevron() {
  return <Icon path={ICONS.chevronRight} className={styles['chevron']} />
}

// ─── Swatch ──────────────────────────────────────────────────────────────────

/** The square that marks a track or check-in beside its name. Decoration. */
export function Swatch({
  colour,
  size = 8,
  shade = 'fill',
  outline = false,
}: {
  readonly colour: string
  readonly size?: number
  readonly shade?: Shade
  /** Hollow, for something that has a colour but no value yet. */
  readonly outline?: boolean
}) {
  const value =
    colour.startsWith('var(') || colour.startsWith('#')
      ? colour
      : paletteVar(colour as PaletteColour, shade)
  const style: CSSProperties = {
    width: `${String(size)}px`,
    height: `${String(size)}px`,
    ...(outline
      ? { border: `1px solid ${value}`, boxSizing: 'border-box' }
      : { background: value }),
  }
  return <span className={styles['swatch']} style={style} aria-hidden="true" />
}

// ─── The page ────────────────────────────────────────────────────────────────

/**
 * A redesigned screen's content: a column with one gap between its blocks.
 *
 * Children are inset by the gutter unless they are full-width bands, which
 * mark themselves `data-bleed`. The gap is the screen's whole vertical
 * rhythm, so children carry no outer margins of their own.
 */
export function Page({ children }: { readonly children: ReactNode }) {
  return (
    <div className={styles['page']} data-bleed>
      {children}
    </div>
  )
}

// ─── Sections and bands ──────────────────────────────────────────────────────

/**
 * A labelled group: a caps label on the page, then a full-width band.
 *
 * `aside` sits opposite the label (a count, a link). `note` is a line of
 * supporting text under the band. Pass `id` to have the label name the band
 * for assistive technology.
 */
export function Section({
  label,
  aside,
  note,
  id,
  padded = false,
  children,
}: {
  readonly label?: ReactNode
  readonly aside?: ReactNode
  readonly note?: ReactNode
  readonly id?: string
  /** One padded block rather than a list of rows. */
  readonly padded?: boolean
  readonly children: ReactNode
}) {
  const labelId = id === undefined ? undefined : `${id}-label`
  return (
    <section className={styles['section']} aria-labelledby={labelId} data-bleed>
      {(label !== undefined || aside !== undefined) && (
        <div className={styles['sectionHead']}>
          {label !== undefined && (
            <h2 id={labelId} className={styles['sectionLabel']}>
              {label}
            </h2>
          )}
          {aside !== undefined && <span className={styles['sectionAside']}>{aside}</span>}
        </div>
      )}
      <div className={padded ? styles['bandPadded'] : styles['band']}>{children}</div>
      {note !== undefined && <p className={styles['sectionNote']}>{note}</p>}
    </section>
  )
}

/** A band with no label, for a screen's own blocks. */
export function Band({
  padded = false,
  className,
  style,
  children,
}: {
  readonly padded?: boolean
  readonly className?: string
  readonly style?: CSSProperties
  readonly children: ReactNode
}) {
  return (
    <div
      className={`${padded ? styles['bandPadded'] : styles['band']} ${className ?? ''}`}
      style={style}
      data-bleed
    >
      {children}
    </div>
  )
}

// ─── Rows ────────────────────────────────────────────────────────────────────

interface RowContent {
  readonly title: ReactNode
  readonly subtitle?: ReactNode
  readonly lead?: ReactNode
  /** Quiet text before the chevron: a count, a status. */
  readonly detail?: ReactNode
  readonly trailing?: ReactNode
}

function RowInner({
  title,
  subtitle,
  lead,
  detail,
  trailing,
  chevron,
}: RowContent & {
  readonly chevron: boolean
}) {
  return (
    <>
      {lead}
      <span className={styles['rowMain']}>
        <span className={styles['rowTitle']}>{title}</span>
        {subtitle !== undefined && (
          <span className={styles['rowSubtitle']}>{subtitle}</span>
        )}
      </span>
      {detail !== undefined && <span className={styles['rowDetail']}>{detail}</span>}
      {trailing}
      {chevron && <Chevron />}
    </>
  )
}

/** A row that goes somewhere. Always a real link, with a chevron. */
export function RowLink(
  props: RowContent & { readonly to: string; readonly label?: string },
) {
  return (
    <Link to={props.to} className={styles['row']} aria-label={props.label}>
      <RowInner {...props} chevron />
    </Link>
  )
}

/** A row that does something in place. */
export function RowButton(
  props: RowContent & {
    readonly onClick: () => void
    readonly chevron?: boolean
    readonly label?: string
  },
) {
  return (
    <button
      type="button"
      className={styles['row']}
      onClick={props.onClick}
      aria-label={props.label}
    >
      <RowInner {...props} chevron={props.chevron ?? false} />
    </button>
  )
}

/** A row that only shows something. */
export function Row(props: RowContent) {
  return (
    <div className={styles['row']}>
      <RowInner {...props} chevron={false} />
    </div>
  )
}

/** The tinted square with an icon that leads a settings row. */
export function Tile({
  colour,
  path,
}: {
  readonly colour: PaletteColour
  readonly path: string
}) {
  return (
    <span
      className={styles['tile']}
      style={{
        background: paletteVar(colour, 'tint'),
        color: paletteVar(colour, 'deep'),
      }}
      aria-hidden="true"
    >
      <Icon path={path} />
    </span>
  )
}

// ─── Headers ─────────────────────────────────────────────────────────────────

/**
 * The top of a sub-screen: back on the left, at most one action on the right.
 *
 * Sub-screens have no bottom navigation (see `routes.tsx`), so this back link
 * is how the user leaves. It is a real link to a real route, not
 * `history.back()`: a screen opened from a bookmark has nothing to go back to.
 */
export function SubHeader({
  back,
  action,
}: {
  readonly back: { readonly to: string; readonly label: string }
  readonly action?: ReactNode
}) {
  return (
    <div className={styles['subHeader']} data-bleed>
      <Link to={back.to} className={styles['headerLink']}>
        <Icon path={ICONS.chevronLeft} />
        {back.label}
      </Link>
      {action}
    </div>
  )
}

/** A header action that navigates, such as "New track". */
export function HeaderLink({
  to,
  icon,
  children,
}: {
  readonly to: string
  readonly icon?: string
  readonly children: ReactNode
}) {
  return (
    <Link to={to} className={styles['headerLink']}>
      {icon !== undefined && <Icon path={icon} />}
      {children}
    </Link>
  )
}

/** A header action that acts in place, such as "Edit". */
export function HeaderButton({
  onClick,
  children,
}: {
  readonly onClick: () => void
  readonly children: ReactNode
}) {
  return (
    <button type="button" className={styles['headerLink']} onClick={onClick}>
      {children}
    </button>
  )
}

/** The page title, and the sentence under it that says what the screen is for. */
export function TitleBlock({
  title,
  lead,
}: {
  readonly title: ReactNode
  readonly lead?: ReactNode
}) {
  return (
    <div className={styles['titleBlock']}>
      <h1>{title}</h1>
      {lead !== undefined && <p className={styles['titleLead']}>{lead}</p>}
    </div>
  )
}

// ─── Choosers ────────────────────────────────────────────────────────────────

export interface Choice<T extends string> {
  readonly value: T
  readonly label: ReactNode
  /**
   * The accessible name, when the visible label is a shortened form of it.
   * Must contain the visible text (WCAG 2.5.3), e.g. "Last 7 days" for "7 days".
   */
  readonly name?: string
  /** Colours the underline of a tab, such as a check-in's own colour. */
  readonly colour?: string
}

/** Equal buttons in a band; the chosen one is tinted. Single choice. */
export function Segmented<T extends string>({
  label,
  labelledBy,
  choices,
  value,
  onChange,
}: {
  readonly label?: string
  readonly labelledBy?: string
  readonly choices: readonly Choice<T>[]
  readonly value: T | null
  readonly onChange: (value: T) => void
}) {
  return (
    <div
      role="group"
      aria-label={label}
      aria-labelledby={labelledBy}
      className={styles['segmented']}
      data-bleed
    >
      {choices.map((choice) => (
        <button
          key={choice.value}
          type="button"
          className={styles['segment']}
          aria-pressed={choice.value === value}
          aria-label={choice.name}
          onClick={() => {
            onChange(choice.value)
          }}
        >
          {choice.label}
        </button>
      ))}
    </div>
  )
}

/** Text options along a hairline; the chosen one is underlined. Single choice. */
export function Tabs<T extends string>({
  label,
  choices,
  value,
  onChange,
  even = false,
}: {
  readonly label: string
  readonly choices: readonly Choice<T>[]
  readonly value: T | null
  readonly onChange: (value: T) => void
  /** Share the width equally rather than sit at their natural widths. */
  readonly even?: boolean
}) {
  return (
    <div
      role="group"
      aria-label={label}
      className={even ? styles['tabsEven'] : styles['tabs']}
      data-bleed
    >
      {choices.map((choice) => (
        <button
          key={choice.value}
          type="button"
          className={styles['tab']}
          aria-pressed={choice.value === value}
          aria-label={choice.name}
          style={
            choice.colour === undefined
              ? undefined
              : ({ '--tab-colour': choice.colour } as CSSProperties)
          }
          onClick={() => {
            onChange(choice.value)
          }}
        >
          {choice.label}
        </button>
      ))}
    </div>
  )
}

// ─── Action bar ──────────────────────────────────────────────────────────────

/**
 * The buttons at the foot of a form or a step: a quiet one and the main one,
 * the main one twice as wide and under the thumb.
 *
 * Sticky to the bottom of the screen, so the way to finish is always in
 * reach however long the form is.
 */
export function ActionBar({ children }: { readonly children: ReactNode }) {
  const count = Array.isArray(children) ? children.filter(Boolean).length : 1
  return (
    <div
      className={count > 1 ? styles['actionBar'] : styles['actionBarSingle']}
      data-bleed
    >
      {children}
    </div>
  )
}

export function ActionButton({
  primary = false,
  icon,
  type = 'button',
  disabled,
  onClick,
  form,
  children,
}: {
  readonly primary?: boolean
  readonly icon?: string | undefined
  readonly type?: 'button' | 'submit'
  readonly disabled?: boolean | undefined
  readonly onClick?: (() => void) | undefined
  readonly form?: string | undefined
  readonly children: ReactNode
}) {
  return (
    <button
      type={type}
      form={form}
      className={primary ? styles['actionPrimary'] : styles['action']}
      disabled={disabled}
      onClick={onClick}
    >
      {children}
      {icon !== undefined && <Icon path={icon} />}
    </button>
  )
}

export function ActionLink({
  to,
  primary = false,
  children,
}: {
  readonly to: string
  readonly primary?: boolean
  readonly children: ReactNode
}) {
  return (
    <Link to={to} className={primary ? styles['actionPrimary'] : styles['action']}>
      {children}
    </Link>
  )
}
